// One row on the material shelf, whatever place it came from.
//
// The shelf shows two unrelated things through one list: the room's own
// material (`LibraryClip` — clips grouped by what makes them sound different)
// and the producer's clip library (`MyClip` — a real API resource). Every
// control on the shelf — search, chips, sort, favourites, recent, the drag
// payload, the ＋ — wants to treat a row as a row, so both resolve to this
// one shape and the components never branch on where a row came from.
//
// Deliberately NOT a copy of either source: it keeps a handle back to the
// original (`clipId` for room material, `mineId` for a library clip) and the
// bits the shelf renders or filters on. Anything heavier (the occurrences, the
// audio source) stays on the source object, reachable through that handle.

import type { MyClip, MyFolder, MyMidi, MyTag, ReferenceClip, ReferenceMidi, ReferenceSeed } from "@shared/api-types.js";
import { fuzzyBest } from "@shared/fuzzy.js";
import { GROUP_ORDER, TAG_GROUPS, parseTag, tagAnswersTo, tagWords, tagsForName as familyTags, tagsSatisfy, validTag } from "@shared/library-tags.js";
import { parseQuery, SUGGESTED_CHIP, UNREVIEWED_CHIP, UNUSED_CHIP, type LibraryQuery } from "@shared/library-search.js";
import type { ClipSource, MidiSource } from "@web-shared/lib/audio-engine";
import type { ClipLibrary, LibraryClip } from "@web-shared/lib/clip-library";
import { midiNotesToSource } from "@web-shared/lib/player-sources";

/** Where rows come from: this room, the library, what was touched lately,
 * or the clips of every seed the producer has been in (reference). */
export type MaterialPlace = "project" | "mine" | "recent" | "seeds";
export type MaterialKind = "loop" | "oneshot" | "midi";

/** What a drag or a ＋ hands to the drop target: a piece of the room, a
 * library clip, a library MIDI idea, or a seed's clip (`referencePayload`). */
export type MaterialPayload = { material: string } | { mine: string } | { mineMidi: string } | { reference: string };

/** A seed's clip as a payload: the seed and the object, in one string. */
export function referencePayload(seedId: string, audioKey: string): string {
  return `${seedId}:${audioKey}`;
}

/** The seed and object a reference payload names; null when malformed. A
 * MIDI clip's "object" is `midi:<key>` (`referenceMidiKey`). */
export function parseReference(ref: string): { seedId: string; audioKey: string } | null {
  const at = ref.indexOf(":");
  if (at <= 0 || at === ref.length - 1) return null;
  return { seedId: ref.slice(0, at), audioKey: ref.slice(at + 1) };
}

const REFERENCE_MIDI = "midi:";
/** A seed's MIDI clip, in the place of an audio key in a reference payload. */
export function referenceMidiKey(key: string): string {
  return `${REFERENCE_MIDI}${key}`;
}
/** The MIDI key a reference "audio key" carries, or null for audio. */
export function referenceMidiOf(audioKey: string): string | null {
  return audioKey.startsWith(REFERENCE_MIDI) ? audioKey.slice(REFERENCE_MIDI.length) : null;
}

export interface MaterialItem {
  /** Stable across rebuilds of the shelf: the library key, or `mine:<id>`. */
  key: string;
  place: Exclude<MaterialPlace, "recent">;
  kind: MaterialKind;
  name: string;
  /** Room material: the track it came from. Library clips have none. */
  trackName: string | null;
  trackIndex: number | null;
  /** 0xRRGGBB, or null when the row should fall back to a palette colour. */
  color: number | null;
  /** Length in beats at the room's tempo; null when unknown (an unwarped
   * library clip with no BPM). */
  beats: number | null;
  seconds: number;
  bpm: number | null;
  /** MIDI clips: how many notes. */
  noteCount: number | null;
  /** Lower-case, deduplicated. Derived for room material, stored for Mine. */
  tags: string[];
  /** Placements in the room (room material); for a library clip, the
   * saved seeds that use it. */
  uses: number;
  /** Stored size for a library clip; 0 for room material and MIDI. */
  bytes: number;
  /** Live's Collections colour (0..6) on a library row; null otherwise. */
  swatch: number | null;
  /** The Session View slot one placement sits in, if any. */
  slot: number | null;
  /** The source the ▶ plays; null when there is nothing to hear. */
  playable: ClipSource | MidiSource | null;
  /** The id a drag or ＋ carries; null when nothing can be placed. */
  payload: MaterialPayload | null;
  /** Added-at for Mine (ISO); null for room material. */
  createdAt: string | null;
  /** Mine: the library folder it sits in (null = the root). Room material
   * has no folder — its tracks are its folders. */
  folderId: string | null;
  /** Mine: tags the browser suggested that are not yet accepted (and not
   * already among `tags`). Drawn dim with a ✓; never filtered on. */
  suggested: string[];
  /** Seeds: the seed this clip belongs to, and its title for the row. */
  seedId: string | null;
  seedTitle: string | null;
  /** Mine: the upload this clip arrived in, so one drop can be reviewed or undone whole. */
  batchId: string | null;
}

// ---- Tags ---------------------------------------------------------------------
//
// Stored tags (shared/library-tags.ts: `drums:kick`, free words) and the
// family words a name gives (`drums`, `bass`…) share one list, so `#dru`
// reaches both. Suggestions the browser made and the producer hasn't
// accepted ride beside the row, never in its filterable tags.

export { tagsForName } from "@shared/library-tags.js";

function unique(tags: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of tags) {
    const k = t.trim().toLowerCase();
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

// ---- From the two sources -------------------------------------------------------

export function itemFromLibraryClip(clip: LibraryClip): MaterialItem {
  const audible = clip.occurrences.find((o) => o.source !== null || (o.midi ?? null) !== null) ?? null;
  const playable = audible?.source ?? audible?.midi ?? null;
  const dragId = (audible ?? clip.occurrences[0])?.clipId ?? null;
  const slot = clip.occurrences.find((o) => o.slot !== undefined)?.slot ?? null;
  const kind: MaterialKind = clip.kind === "midi" ? "midi" : clip.looping || clip.durationBeats >= 4 ? "loop" : "oneshot";
  const tags = unique([
    kind,
    ...(slot !== null ? ["session"] : []),
    ...familyTags(clip.trackName),
    ...familyTags(clip.name),
    ...familyTags(clip.sourceFile ?? ""),
  ]);
  return {
    key: clip.key,
    place: "project",
    kind,
    name: clip.name,
    trackName: clip.trackName,
    trackIndex: clip.trackIndex,
    color: clip.color,
    beats: clip.durationBeats,
    seconds: clip.durationSec,
    bpm: null,
    noteCount: clip.kind === "midi" ? (clip.noteCount ?? 0) : null,
    tags,
    uses: clip.occurrences.length,
    bytes: 0,
    swatch: null,
    slot,
    playable,
    payload: dragId ? { material: dragId } : null,
    createdAt: null,
    folderId: null,
    suggested: [],
    seedId: null,
    seedTitle: null,
    batchId: null,
  };
}

/** A clip of a seed the producer has been in, as a row: it previews from
 * the object itself, and ＋ keeps it into Mine on the way to the room. */
export function itemFromReference(seed: ReferenceSeed, clip: ReferenceClip, audioUrl: string): MaterialItem {
  const beats = clip.durationBeats;
  const seconds = beats !== null && seed.bpm > 0 ? (beats * 60) / seed.bpm : 0;
  const kind: MaterialKind = beats !== null && beats < 4 ? "oneshot" : "loop";
  const key = referenceKey(seed.id, clip.audioKey);
  return {
    key,
    place: "seeds",
    kind,
    name: clip.name,
    trackName: clip.trackName,
    trackIndex: clip.trackIndex,
    color: clip.color,
    beats,
    seconds,
    bpm: seed.bpm > 0 ? seed.bpm : null,
    noteCount: null,
    tags: unique([kind, ...familyTags(clip.trackName), ...familyTags(clip.name)]),
    uses: 0,
    bytes: 0,
    swatch: null,
    slot: null,
    playable: { id: key, laneId: "reference", url: audioUrl, startSec: 0, ...(seconds > 0 ? { durationSec: seconds } : {}) },
    payload: { reference: referencePayload(seed.id, clip.audioKey) },
    createdAt: seed.touchedAt,
    folderId: null,
    suggested: [],
    seedId: seed.id,
    seedTitle: seed.title,
    batchId: null,
  };
}

/** The key a seed's clip row carries (and its star is kept under). */
export function referenceKey(seedId: string, audioKey: string): string {
  return `ref:${seedId}:${audioKey}`;
}

/** A seed's MIDI clip as a row: it previews through the preview instrument
 * at the room's tempo, as a library idea does, and Keep makes it one. */
export function itemFromReferenceMidi(seed: ReferenceSeed, midi: ReferenceMidi, tempo: number): MaterialItem {
  const bpm = tempo > 0 ? tempo : 120;
  const seconds = (midi.durationBeats * 60) / bpm;
  const key = referenceKey(seed.id, referenceMidiKey(midi.key));
  return {
    key,
    place: "seeds",
    kind: "midi",
    name: midi.name,
    trackName: midi.trackName,
    trackIndex: midi.trackIndex,
    color: midi.color,
    beats: midi.durationBeats,
    seconds,
    bpm: null,
    noteCount: midi.noteCount,
    tags: unique(["midi", ...familyTags(midi.trackName), ...familyTags(midi.name)]),
    uses: 0,
    bytes: 0,
    swatch: null,
    slot: null,
    playable: midiNotesToSource(midi.notes, bpm, { id: key, laneId: "reference", startSec: 0, durationSec: seconds }),
    payload: { reference: referencePayload(seed.id, referenceMidiKey(midi.key)) },
    createdAt: seed.touchedAt,
    folderId: null,
    suggested: [],
    seedId: seed.id,
    seedTitle: seed.title,
    batchId: null,
  };
}

export function itemsFromReference(seeds: readonly ReferenceSeed[], urlOf: (audioKey: string) => string, tempo = 120): MaterialItem[] {
  // Keys are unique, whatever the server sent: a row's key is the React key
  // of its element, and two elements under one key leave the second behind
  // when the list changes — a ghost row from Seeds still standing in Mine.
  const seen = new Set<string>();
  const out: MaterialItem[] = [];
  for (const seed of seeds) {
    for (const item of [...seed.clips.map((clip) => itemFromReference(seed, clip, urlOf(clip.audioKey))), ...(seed.midi ?? []).map((midi) => itemFromReferenceMidi(seed, midi, tempo))]) {
      if (seen.has(item.key)) continue;
      seen.add(item.key);
      out.push(item);
    }
  }
  return out;
}

/**
 * A TagDelta applied to a stored tag list, the way the server will: `set`
 * confirms (creating as the producer's own when nothing suggested it),
 * `unset` removes. For the optimistic cache, so a tap reads as done.
 */
export function applyTagDelta(tags: readonly MyTag[], delta: { set?: string[]; unset?: string[]; suggest?: string[] }): MyTag[] {
  const unset = new Set(delta.unset ?? []);
  const out: MyTag[] = tags.filter((t) => !unset.has(t.tag)).map((t) => (delta.set?.includes(t.tag) ? { ...t, confirmed: true } : t));
  for (const t of delta.set ?? []) if (!unset.has(t) && !out.some((x) => x.tag === t)) out.push({ tag: t, source: "user", confirmed: true });
  // A folder's suggestion lands only where nothing by that name is.
  for (const t of delta.suggest ?? []) if (!unset.has(t) && !out.some((x) => x.tag === t)) out.push({ tag: t, source: "folder", confirmed: false });
  return out;
}

/**
 * A review applied to a stored tag list, the way the server will: every
 * waiting suggestion (or only the named ones) confirmed when `keep`, else
 * dropped. Confirmed tags are never touched. For the optimistic cache.
 */
export function applyReview(tags: readonly MyTag[], review: { keep: boolean; tags?: readonly string[] }): MyTag[] {
  const named = review.tags ? new Set(review.tags) : null;
  const out: MyTag[] = [];
  for (const t of tags) {
    if (t.confirmed || (named && !named.has(t.tag))) out.push(t);
    else if (review.keep) out.push({ ...t, confirmed: true });
  }
  return out;
}

/** Confirmed tags, and the suggestions not already covered by `tags`. */
function splitTags(stored: readonly MyTag[], derived: readonly string[]): { tags: string[]; suggested: string[] } {
  const tags = unique([...derived, ...stored.filter((t) => t.confirmed).map((t) => t.tag)]);
  const suggested = unique(stored.filter((t) => !t.confirmed).map((t) => t.tag)).filter((t) => !tags.includes(t));
  return { tags, suggested };
}

/**
 * A library clip, as a row. `tempo` is the room's: a loop with a BPM is shown
 * in bars at that tempo (which is how it will be placed); a one-shot or a
 * clip with no BPM is shown in seconds.
 */
export function itemFromMyClip(clip: MyClip, tempo: number, audioUrl: string): MaterialItem {
  const seconds = clip.durationMs / 1000;
  const kind: MaterialKind = clip.kind === "loop" ? "loop" : "oneshot";
  const beats = clip.bpm !== null && clip.bpm > 0 ? (seconds * clip.bpm) / 60 : kind === "loop" && tempo > 0 ? (seconds * tempo) / 60 : null;
  const { tags, suggested } = splitTags(clip.tags ?? [], [kind, ...familyTags(clip.name)]);
  return {
    key: `mine:${clip.id}`,
    place: "mine",
    kind,
    name: clip.name,
    trackName: null,
    trackIndex: null,
    color: null,
    beats,
    seconds,
    bpm: clip.bpm,
    noteCount: null,
    tags,
    uses: clip.uses ?? 0,
    bytes: clip.sizeBytes,
    swatch: clip.color ?? null,
    slot: null,
    playable: { id: `mine:${clip.id}`, laneId: "mine", url: audioUrl, startSec: 0, durationSec: seconds },
    payload: { mine: clip.id },
    createdAt: clip.createdAt,
    folderId: clip.folderId,
    suggested,
    seedId: null,
    seedTitle: null,
    batchId: clip.batchId ?? null,
  };
}

/** The key a library MIDI idea's row carries (and its star is kept under). */
export function midiKey(id: string): string {
  return `midi:${id}`;
}

/** A library MIDI idea, as a row. It previews at the room's tempo through
 * the preview instrument, as the Project place's MIDI does. */
export function itemFromMyMidi(midi: MyMidi, tempo: number): MaterialItem {
  const bpm = tempo > 0 ? tempo : 120;
  const seconds = (midi.durationBeats * 60) / bpm;
  const playable = midiNotesToSource(midi.notes, bpm, { id: midiKey(midi.id), laneId: "mine", startSec: 0, durationSec: seconds });
  const { tags, suggested } = splitTags(midi.tags ?? [], ["midi", ...familyTags(midi.name)]);
  return {
    key: midiKey(midi.id),
    place: "mine",
    kind: "midi",
    name: midi.name,
    trackName: null,
    trackIndex: null,
    color: null,
    beats: midi.durationBeats,
    seconds,
    bpm: null,
    noteCount: midi.noteCount,
    tags,
    uses: 0,
    bytes: 0,
    swatch: midi.color ?? null,
    slot: null,
    playable,
    payload: { mineMidi: midi.id },
    createdAt: midi.createdAt,
    folderId: midi.folderId,
    suggested,
    seedId: null,
    seedTitle: null,
    batchId: null,
  };
}

// ---- Folders (Mine) -----------------------------------------------------------------
//
// Live's User Library and Note's Externals are folders: WHERE, as the chips
// are WHAT. The tree is small and read whole, so these walk it in memory.

/** The folders directly under `parentId` (null = the root), in their order. */
export function childFolders(folders: readonly MyFolder[], parentId: string | null): MyFolder[] {
  return folders
    .filter((f) => f.parentId === parentId)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
}

/** Root first, then every folder down to `id`: the path line. Empty at the root. */
/**
 * Where a folder may move: not into itself or anything under it, not where
 * it already is, and not so deep that its own subtree would pass the limit
 * — the same rule the server applies, so the picker says no before a round
 * trip does. `null` is the root.
 */
export function canMoveFolder(folders: readonly MyFolder[], id: string, to: string | null, maxDepth: number): boolean {
  const folder = folders.find((f) => f.id === id);
  if (!folder || to === folder.parentId) return false;
  if (to !== null && folderSubtree(folders, id).has(to)) return false;
  // Depth of the target (root = 0, a root folder = 1) plus the moving
  // folder and the levels under it.
  const depth = to === null ? 0 : folderPath(folders, to).length;
  const height = (fid: string): number => Math.max(0, ...childFolders(folders, fid).map((c) => 1 + height(c.id)));
  return depth + 1 + height(id) <= maxDepth;
}

export function folderPath(folders: readonly MyFolder[], id: string | null): MyFolder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const out: MyFolder[] = [];
  const seen = new Set<string>();
  for (let cur = id; cur !== null && !seen.has(cur); ) {
    const f = byId.get(cur);
    if (!f) break;
    seen.add(cur);
    out.unshift(f);
    cur = f.parentId;
  }
  return out;
}

/** `id` and every folder under it. */
export function folderSubtree(folders: readonly MyFolder[], id: string | null): Set<string | null> {
  const out = new Set<string | null>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of folders) {
      if (out.has(f.id)) continue;
      if (out.has(f.parentId)) {
        out.add(f.id);
        grew = true;
      }
    }
  }
  return out;
}

/** How many items sit in each folder, counting everything underneath it. */
export function folderCounts(folders: readonly MyFolder[], items: readonly MaterialItem[]): Map<string, number> {
  const parentOf = new Map(folders.map((f) => [f.id, f.parentId]));
  const counts = new Map<string, number>();
  for (const it of items) {
    for (let cur = it.folderId; cur !== null; cur = parentOf.get(cur) ?? null) counts.set(cur, (counts.get(cur) ?? 0) + 1);
  }
  return counts;
}

/** Bytes stored under each folder, subfolders included — the storage view. */
export function folderBytes(folders: readonly MyFolder[], items: readonly MaterialItem[]): Map<string, number> {
  const parentOf = new Map(folders.map((f) => [f.id, f.parentId]));
  const bytes = new Map<string, number>();
  for (const it of items) {
    if (it.bytes === 0) continue;
    for (let cur = it.folderId; cur !== null; cur = parentOf.get(cur) ?? null) bytes.set(cur, (bytes.get(cur) ?? 0) + it.bytes);
  }
  return bytes;
}

/** Every row of a room, in track order then placement order. */
export function itemsFromLibrary(library: ClipLibrary): MaterialItem[] {
  return library.tracks
    .flatMap((t) => t.clips)
    .sort((a, b) => a.trackIndex - b.trackIndex || (a.occurrences[0]?.start ?? 0) - (b.occurrences[0]?.start ?? 0))
    .map(itemFromLibraryClip);
}

// ---- Search and chips --------------------------------------------------------------

/**
 * What the search box means. Live's rule: every word must match (AND), and a
 * `#word` is a tag rather than text. Case-insensitive throughout. The parser
 * lives in shared/ so a smart folder reads the same on the hub.
 */
export type MaterialQuery = LibraryQuery;
export { parseQuery };

/** The chips the shelf offers, in the order they are shown. `suggested`
 * lets `#words` and tag chips reach a row's suggestions ("Suggested too");
 * `unreviewed` keeps rows that still have one ("Needs review"). */
export type MaterialChip = MaterialKind | "favorite" | "suggested" | "unreviewed" | "unused" | `track:${number}` | `tag:${string}` | `color:${number}` | `seed:${string}` | `batch:${string}`;

export interface MaterialFilter {
  query: MaterialQuery;
  chips: ReadonlySet<MaterialChip>;
  favorites: ReadonlySet<string>;
}

function haystack(item: MaterialItem): string {
  return `${item.name} ${item.trackName ?? ""} ${item.seedTitle ?? ""}`.toLowerCase();
}

export function matches(item: MaterialItem, filter: MaterialFilter): boolean {
  const hay = haystack(item);
  for (const w of filter.query.words) if (!hay.includes(w)) return false;
  // Chips of one family OR together (loops OR one-shots), families AND.
  const kinds = new Set<MaterialKind>();
  const tracks = new Set<number>();
  const tags = new Set<string>();
  let favorite = false;
  let suggested = false;
  let unreviewed = false;
  let unused = false;
  const colors = new Set<number>();
  const seeds = new Set<string>();
  let batch: string | null = null;
  for (const chip of filter.chips) {
    if (chip === "favorite") favorite = true;
    else if (chip.startsWith("seed:")) seeds.add(chip.slice(5));
    else if (chip.startsWith("batch:")) batch = chip.slice(6);
    else if (chip === SUGGESTED_CHIP) suggested = true;
    else if (chip === UNREVIEWED_CHIP) unreviewed = true;
    else if (chip === UNUSED_CHIP) unused = true;
    else if (chip.startsWith("color:")) colors.add(Number(chip.slice(6)));
    else if (chip.startsWith("track:")) tracks.add(Number(chip.slice(6)));
    else if (chip.startsWith("tag:")) tags.add(chip.slice(4));
    else if (chip === "loop" || chip === "oneshot" || chip === "midi") kinds.add(chip);
  }
  // The same rule as shared/library-search.ts rowMatches: suggestions are
  // in reach only when the search asked for them.
  const reach = suggested && item.suggested.length > 0 ? [...item.tags, ...item.suggested] : item.tags;
  for (const t of filter.query.tags) if (!reach.some((tag) => tagAnswersTo(tag, t))) return false;
  if (kinds.size > 0 && !kinds.has(item.kind)) return false;
  if (tracks.size > 0 && (item.trackIndex === null || !tracks.has(item.trackIndex))) return false;
  // Values of one group OR together (Kick or Snare); groups and free words AND.
  if (!tagsSatisfy(reach, [...tags])) return false;
  if (favorite && !filter.favorites.has(item.key)) return false;
  if (unreviewed && item.suggested.length === 0) return false;
  if (unused && (item.place !== "mine" || item.uses > 0)) return false;
  if (colors.size > 0 && (item.swatch === null || !colors.has(item.swatch))) return false;
  if (seeds.size > 0 && (item.seedId === null || !seeds.has(item.seedId))) return false;
  if (batch !== null && item.batchId !== batch) return false;
  return true;
}

/** The suggestions a row matched on, for the row to show dashed: those a
 * `#word` or a tag chip of the filter reaches; every suggestion when the
 * filter names no tag (the Needs review queue). Empty unless `suggested`
 * or `unreviewed` is on. */
export function matchedSuggestions(item: MaterialItem, filter: MaterialFilter): string[] {
  const on = filter.chips.has(SUGGESTED_CHIP) || filter.chips.has(UNREVIEWED_CHIP);
  if (!on || item.suggested.length === 0) return [];
  const chosen = [...filter.chips].filter((c) => c.startsWith("tag:")).map((c) => c.slice(4));
  const wanted = [...filter.query.tags, ...chosen];
  if (wanted.length === 0) return item.suggested;
  return item.suggested.filter((s) => wanted.some((w) => tagAnswersTo(s, w)));
}

/** One completion for the Add… box: a tag, how it reads, and its group. */
export interface TagCompletion {
  tag: string;
  label: string;
  group: string | null;
  /** The typed word itself, offered as a new free tag. */
  free?: boolean;
}

const KIND_WORDS = new Set(["loop", "oneshot", "midi", "session"]);
const FAMILY_WORDS = new Set(["drums", "bass", "keys", "vocals", "instruments", "fx"]);

/** Fuzzy matches need this many characters; shorter prefixes match by prefix alone. */
const FUZZY_MIN = 3;

/** How well a typed word fits a tag: 3 exact, 2 a prefix of one of its
 * words, 1 + score/100 a fuzzy hit over them, 0 nothing. */
function fit(tag: string, p: string): number {
  const words = tagWords(tag);
  if (words.includes(p)) return 3;
  if (words.some((w) => w.startsWith(p))) return 2;
  if (p.length < FUZZY_MIN) return 0;
  const score = fuzzyBest(p, words);
  return score > 0 ? 1 + score / 100 : 0;
}

/**
 * What Add… offers for a typed prefix: the fixed groups' values and the free
 * tags already in use. Exact matches first, then prefixes of a tag's id, a
 * label word or an alias (`hat` → Hi-hat, `vox` → Voice), then fuzzy hits
 * (`snr` → Snare, `hhat` → Hi-hat) by score — group tags in GROUP_ORDER,
 * free tags by how many rows carry them. "#word as a free tag" comes last.
 * `drums:` lists that group. Never a tempo (suggestions only).
 */
export function completeVocabulary(items: readonly MaterialItem[], prefix: string, limit = 8): TagCompletion[] {
  const p = prefix.trim().toLowerCase().replace(/^#/, "");
  if (p === "") return [];
  const groups = TAG_GROUPS.filter((g) => g.values !== null);
  const out: TagCompletion[] = [];
  const seen = new Set<string>();
  const push = (c: TagCompletion): void => {
    if (!seen.has(c.tag)) {
      seen.add(c.tag);
      out.push(c);
    }
  };
  const colon = p.indexOf(":");
  if (colon >= 0) {
    const g = groups.find((x) => x.id === p.slice(0, colon));
    const rest = p.slice(colon + 1);
    for (const v of g?.values ?? []) if (rest === "" || fit(`${g!.id}:${v.id}`, rest) > 0) push({ tag: `${g!.id}:${v.id}`, label: v.label, group: g!.label });
    return out.slice(0, limit);
  }
  // Free tags in use, by count.
  const counts = new Map<string, number>();
  for (const it of items) for (const t of [...it.tags, ...it.suggested]) if (parseTag(t).group === null && !KIND_WORDS.has(t) && !FAMILY_WORDS.has(t)) counts.set(t, (counts.get(t) ?? 0) + 1);
  const ranked: Array<TagCompletion & { fit: number; order: number; count: number }> = [];
  for (const g of groups) {
    for (const v of g.values!) {
      const tag = `${g.id}:${v.id}`;
      const f = fit(tag, p);
      if (f > 0) ranked.push({ tag, label: v.label, group: g.label, fit: f, order: GROUP_ORDER.indexOf(g.id), count: 0 });
    }
  }
  for (const [t, count] of counts) {
    const f = fit(t, p);
    if (f > 0) ranked.push({ tag: t, label: `#${t}`, group: null, fit: f, order: GROUP_ORDER.length, count });
  }
  // Exact, then prefix, then fuzzy by score; within a tier a group tag in
  // GROUP_ORDER, then a free tag by use.
  ranked.sort((a, b) => Math.floor(b.fit) - Math.floor(a.fit) || b.fit - a.fit || a.order - b.order || b.count - a.count || a.label.localeCompare(b.label));
  for (const r of ranked) push(r);
  const free = validTag(p) && parseTag(p).group === null && !counts.has(p) ? { tag: p, label: `#${p}`, group: null, free: true } : null;
  const kept = out.slice(0, free ? limit - 1 : limit);
  return free ? [...kept, free] : kept;
}

/** Tag names that complete a `#` prefix — the tags in use, confirmed and
 * suggested: exact, then prefix (id, label word, alias), then fuzzy; ties
 * by how many rows carry the tag. */
export function completeTag(items: readonly MaterialItem[], prefix: string, limit = 6): string[] {
  const p = prefix.toLowerCase();
  if (p === "") return [];
  const counts = new Map<string, number>();
  for (const item of items) for (const t of new Set([...item.tags, ...item.suggested])) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts.entries()]
    .map(([t, count]) => ({ t, count, fit: fit(t, p) }))
    .filter((x) => x.fit > 0)
    .sort((a, b) => Math.floor(b.fit) - Math.floor(a.fit) || b.fit - a.fit || b.count - a.count || a.t.localeCompare(b.t))
    .slice(0, limit)
    .map((x) => x.t);
}

/**
 * "Did you mean…": for a `#word` no row answers, the vocabulary tag it most
 * likely meant — a prefix of a word of some tag, else the best fuzzy hit —
 * or null when nothing comes close. The evaluator never guesses; this is
 * offered under the search box as a tap.
 */
export function suggestTagFor(items: readonly MaterialItem[], word: string): string | null {
  const p = word.trim().toLowerCase().replace(/^#/, "");
  if (p.length < 2) return null;
  const candidates = new Set<string>();
  for (const g of TAG_GROUPS) for (const v of g.values ?? []) candidates.add(`${g.id}:${v.id}`);
  for (const it of items) for (const t of [...it.tags, ...it.suggested]) if (!KIND_WORDS.has(t) && !FAMILY_WORDS.has(t)) candidates.add(t);
  let best: { tag: string; fit: number } | null = null;
  for (const tag of candidates) {
    const f = fit(tag, p);
    if (f > 0 && (best === null || f > best.fit)) best = { tag, fit: f };
  }
  return best?.tag ?? null;
}

// ---- Sort ---------------------------------------------------------------------------

export type MaterialSort = "track" | "name" | "length" | "recent" | "uses" | "size";

export const SORT_LABELS: Record<MaterialSort, string> = {
  track: "Track",
  name: "Name",
  length: "Length",
  recent: "Newest",
  uses: "Most used",
  size: "Largest",
};

export function sortItems(items: readonly MaterialItem[], by: MaterialSort): MaterialItem[] {
  const out = [...items];
  const name = (a: MaterialItem, b: MaterialItem) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
  switch (by) {
    case "name":
      return out.sort(name);
    case "length":
      return out.sort((a, b) => a.seconds - b.seconds || name(a, b));
    case "recent":
      return out.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "") || name(a, b));
    case "uses":
      return out.sort((a, b) => b.uses - a.uses || name(a, b));
    case "size":
      return out.sort((a, b) => b.bytes - a.bytes || name(a, b));
    case "track":
    default:
      // Already in track order from the library; Mine rows (no track) keep
      // their own order after any room rows.
      return out.sort((a, b) => (a.trackIndex ?? 1e9) - (b.trackIndex ?? 1e9));
  }
}

/** The default sort for a place: Live lists Current Project by position and the User Library by name — we prefer newest for Mine. */
export function defaultSort(place: MaterialPlace): MaterialSort {
  return place === "project" ? "track" : "recent";
}

// ---- Labels ---------------------------------------------------------------------------

/** "2 bars" / "1.5 beats" / "0.4 s" — the same reading Live gives in the browser. */
export function lengthLabel(item: MaterialItem): string {
  if (item.beats === null) return item.seconds < 10 ? `${item.seconds.toFixed(1)} s` : `${Math.round(item.seconds)} s`;
  const bars = item.beats / 4;
  const rounded = Math.round(bars);
  if (bars >= 1 && Math.abs(bars - rounded) < 1e-4) return `${rounded} bar${rounded === 1 ? "" : "s"}`;
  const b = Math.round(item.beats * 100) / 100;
  return `${b} beat${b === 1 ? "" : "s"}`;
}
